import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, ArrowLeft, Sparkles, Check, Crown, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const UpgradePage = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/payments/checkout`,
        { 
          package_id: 'premium', 
          origin_url: window.location.origin 
        },
        { headers: { 'user-id': user.id } }
      );
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  const basicFeatures = [
    'AI Wedding Planner (Grace)',
    'Progressive Task Checklist',
    'Guest List Management',
    'Budget Tracking',
    'Guest Website Builder',
  ];

  const premiumFeatures = [
    'Everything in Essentials',
    'Priority AI Responses',
    'Advanced Budget Analytics',
    'Vendor Recommendations',
    'Seating Chart Builder',
    'Day-of Timeline Generator',
    'Priority Support',
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAF9] to-[#F5F5F4]">
      {/* Header */}
      <div className="bg-white/70 backdrop-blur-xl border-b border-stone-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Button
            data-testid="back-to-dashboard-btn"
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-[#57534E] hover:text-[#881337]"
          >
            <ArrowLeft className="mr-2" size={18} />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Heart className="text-[#881337]" size={24} fill="#881337" />
            <span className="text-xl font-serif font-bold tracking-tight text-[#1C1917]">VowFlow</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-rose-50 text-[#881337] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Crown size={16} />
            Upgrade Your Experience
          </div>
          <h1 className="text-4xl md:text-5xl font-serif font-light tracking-tight text-[#1C1917] mb-4">
            Elevate Your Wedding Planning
          </h1>
          <p className="text-lg text-[#57534E] max-w-2xl mx-auto">
            Unlock premium features designed to make your wedding planning journey even more magical
          </p>
        </div>

        {/* Plans Comparison */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Essentials Plan */}
          <div className="bg-white rounded-3xl p-8 border border-stone-200">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Star className="text-stone-400" size={24} />
                <h2 className="text-2xl font-serif text-[#1C1917]">Essentials</h2>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-serif text-[#1C1917]">$9.99</span>
                <span className="text-[#57534E]">/month</span>
              </div>
            </div>

            <div className="space-y-4 mb-8">
              {basicFeatures.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <Check className="text-[#84A98C] flex-shrink-0" size={20} />
                  <span className="text-[#57534E]">{feature}</span>
                </div>
              ))}
            </div>

            {user.subscription_tier === 'basic' && (
              <div className="bg-stone-100 text-center py-3 rounded-full text-[#57534E] font-medium">
                Current Plan
              </div>
            )}
          </div>

          {/* Premium Plan */}
          <div className="bg-gradient-to-br from-[#881337] to-[#9F1239] rounded-3xl p-8 text-white relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative">
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="text-yellow-300" size={24} />
                  <h2 className="text-2xl font-serif">Premium</h2>
                  <span className="bg-white/20 px-2 py-0.5 rounded text-xs font-medium">RECOMMENDED</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-serif">$14.99</span>
                  <span className="text-rose-200">/month</span>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                {premiumFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check className="text-yellow-300 flex-shrink-0" size={20} />
                    <span className="text-rose-100">{feature}</span>
                  </div>
                ))}
              </div>

              {user.subscription_tier === 'premium' ? (
                <div className="bg-white/20 text-center py-3 rounded-full font-medium">
                  Current Plan
                </div>
              ) : (
                <Button
                  data-testid="upgrade-to-premium-btn"
                  onClick={handleUpgrade}
                  disabled={loading}
                  className="w-full bg-white text-[#881337] hover:bg-rose-50 rounded-full py-6 text-lg font-medium"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Sparkles size={20} />
                      Upgrade to Premium
                    </span>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* FAQ or Additional Info */}
        <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-8 border border-white/50 text-center">
          <h3 className="text-xl font-serif text-[#1C1917] mb-2">Questions about upgrading?</h3>
          <p className="text-[#57534E] mb-4">
            Chat with Grace, your AI wedding planner, anytime for help with your planning journey.
          </p>
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="rounded-full"
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UpgradePage;
